/**
 * AJAX Service
 * Generic HTTP request handler with error handling
 * Supports both Logto authentication and development mode
 */

import { getBaseUrl } from './utils';
import { getAccessToken as getLogtoAccessToken, isTokenBridgeActive } from './tokenBridge';
import { isLogtoEnabled } from '@/providers/LogtoProviderWrapper';

function getApiBaseUrl(): string {
  return getBaseUrl();
}

interface RequestOptions extends RequestInit {
  timeout?: number;
  skipAuth?: boolean;
}

/**
 * Get auth token - supports both Logto and legacy localStorage
 */
async function getAuthToken(): Promise<string | null> {
  // If Logto is enabled and token bridge is active, use Logto token
  if (isLogtoEnabled() && isTokenBridgeActive()) {
    try {
      console.log('[Ajax] Getting Logto access token...');
      const token = await getLogtoAccessToken();
      console.log(`[Ajax] Logto token ${token ? 'obtained' : 'is null'}`);
      return token;
    } catch (error) {
      console.error('[Ajax] Failed to get Logto access token:', error);
      return null;
    }
  }

  console.log(`[Ajax] Auth mode: logtoEnabled=${isLogtoEnabled()}, bridgeActive=${isTokenBridgeActive()}`);

  // Fallback to localStorage for dev mode
  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      const { state } = JSON.parse(authStorage);
      // In dev mode, we just need to check if authenticated
      if (state?.isAuthenticated && state?.isDevMode) {
        return 'dev-mode-token';
      }
    }
  } catch (error) {
    console.error('[Ajax] Failed to get auth token:', error);
  }
  return null;
}

/**
 * Add auth headers to request options
 */
async function addAuthHeaders(headers: HeadersInit = {}, skipAuth = false): Promise<HeadersInit> {
  if (skipAuth) return headers;

  // If caller already provided Authorization header, don't override it.
  try {
    if (headers instanceof Headers) {
      if (headers.has('Authorization')) return headers;
    } else if (Array.isArray(headers)) {
      const hasAuth = headers.some(([k]) => k.toLowerCase() === 'authorization');
      if (hasAuth) return headers;
    } else if (headers && typeof headers === 'object') {
      const keys = Object.keys(headers as Record<string, string>);
      if (keys.some((k) => k.toLowerCase() === 'authorization')) return headers;
    }
  } catch {
    // ignore and continue
  }

  const token = await getAuthToken();
  if (token && token !== 'dev-mode-token') {
    return {
      ...headers,
      'Authorization': `Bearer ${token}`,
    };
  }

  return headers;
}

interface APIError {
  detail?: string;
  message?: string;
}

/**
 * Generic fetch with timeout and error handling
 */
async function fetchWithTimeout(
  url: string,
  options: RequestOptions = {}
): Promise<Response> {
  const {
    timeout = 600000,  // 10分钟超时 (10 * 60 * 1000)
    ...fetchOptions
  } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const error: APIError = await response.json().catch(() => ({}));
      console.error(`[Ajax] Request failed: ${response.status} ${response.statusText}`, {
        url: url,
        detail: error.detail,
        message: error.message,
      });
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`);
    }

    throw error;
  }
}

/**
 * GET request
 */
export async function get<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>,
  options?: RequestOptions
): Promise<T> {
  const url = new URL(`${getApiBaseUrl()}${endpoint}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  const skipAuth = options?.skipAuth ?? false;
  const headers = await addAuthHeaders(options?.headers, skipAuth);

  const response = await fetchWithTimeout(url.toString(), {
    ...options,
    method: 'GET',
    headers,
  });

  return response.json();
}

/**
 * POST request
 */
export async function post<T>(
  endpoint: string,
  data?: unknown,
  options?: RequestOptions & { params?: Record<string, string | number | boolean | undefined> },
): Promise<T> {
  let url = `${getApiBaseUrl()}${endpoint}`;

  // Add query params if provided
  if (options?.params) {
    const queryParams = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.set(key, String(value));
      }
    });
    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  const { params, ...fetchOptions } = options || {};
  const skipAuth = options?.skipAuth ?? false;
  const headers = await addAuthHeaders({
    'Content-Type': 'application/json',
    ...fetchOptions?.headers,
  }, skipAuth);

  const response = await fetchWithTimeout(url, {
    ...fetchOptions,
    method: 'POST',
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  return response.json();
}

/**
 * PUT request
 */
export async function put<T>(
  endpoint: string,
  data?: unknown,
  options?: RequestOptions
): Promise<T> {
  const skipAuth = options?.skipAuth ?? false;
  const headers = await addAuthHeaders({
    'Content-Type': 'application/json',
    ...options?.headers,
  }, skipAuth);

  const response = await fetchWithTimeout(`${getApiBaseUrl()}${endpoint}`, {
    ...options,
    method: 'PUT',
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  return response.json();
}

/**
 * PATCH request
 */
export async function patch<T>(
  endpoint: string,
  data?: unknown,
  options?: RequestOptions
): Promise<T> {
  const skipAuth = options?.skipAuth ?? false;
  const headers = await addAuthHeaders({
    'Content-Type': 'application/json',
    ...options?.headers,
  }, skipAuth);

  const response = await fetchWithTimeout(`${getApiBaseUrl()}${endpoint}`, {
    ...options,
    method: 'PATCH',
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  return response.json();
}

/**
 * DELETE request
 */
export async function del<T>(
  endpoint: string,
  options?: RequestOptions
): Promise<T> {
  const skipAuth = options?.skipAuth ?? false;
  const headers = await addAuthHeaders(options?.headers, skipAuth);

  const response = await fetchWithTimeout(`${getApiBaseUrl()}${endpoint}`, {
    ...options,
    method: 'DELETE',
    headers,
  });

  return response.json();
}

/**
 * Form data POST (for file uploads)
 */
export async function postForm<T>(
  endpoint: string,
  formData: FormData,
  options?: RequestOptions
): Promise<T> {
  const response = await fetchWithTimeout(`${getApiBaseUrl()}${endpoint}`, {
    ...options,
    method: 'POST',
    body: formData,
  });

  return response.json();
}

export { getApiBaseUrl };
